import { BaseApiClient } from './base-client';
import { UserSettingsResponse, UserSettingsUpdate, NotificationSettings } from '../types/settings';

export class SettingsApiClient extends BaseApiClient {
  async getUserSettings(): Promise<UserSettingsResponse> {
    return this.get<UserSettingsResponse>('/api/v1/settings/');
  }

  async updateUserSettings(settings: UserSettingsUpdate): Promise<UserSettingsResponse> {
    return this.put<UserSettingsResponse>('/api/v1/settings/', settings);
  }

  // Helper method to get available themes
  getAvailableThemes(): Array<{ value: string; label: string }> {
    return [
      { value: 'light', label: 'Light' },
      { value: 'dark', label: 'Dark' },
      { value: 'system', label: 'System' }
    ];
  }

  // Helper method to get available languages
  getAvailableLanguages(): Array<{ value: string; label: string }> {
    return [
      { value: 'en', label: 'English' },
      { value: 'lt', label: 'Lithuanian' }
    ];
  }

  // Helper method to validate settings
  validateSettings(settings: UserSettingsUpdate): string[] {
    const errors: string[] = [];

    if (settings.theme && !['light', 'dark', 'system'].includes(settings.theme)) {
      errors.push('Invalid theme selection');
    }

    if (settings.language && !['en', 'lt'].includes(settings.language)) {
      errors.push('Invalid language selection');
    }

    return errors;
  }

  // Helper method to get default settings
  getDefaultSettings(): UserSettingsResponse {
    return {
      notifications: {
        email: true,
        browser: true
      },
      language: 'en',
      theme: 'light'
    };
  }

  // Helper method to merge settings with defaults
  mergeWithDefaults(settings: Partial<UserSettingsResponse>): UserSettingsResponse {
    const defaults = this.getDefaultSettings();
    return {
      notifications: {
        ...defaults.notifications,
        ...settings.notifications
      },
      language: settings.language || defaults.language,
      theme: settings.theme || defaults.theme
    };
  }

  // Helper method to compare settings for changes
  hasSettingsChanged(current: UserSettingsResponse, updated: UserSettingsUpdate): boolean {
    if (updated.theme && updated.theme !== current.theme) return true;
    if (updated.language && updated.language !== current.language) return true;
    if (updated.notifications) {
      if (updated.notifications.email !== undefined && updated.notifications.email !== current.notifications.email) return true;
      if (updated.notifications.browser !== undefined && updated.notifications.browser !== current.notifications.browser) return true;
    }
    return false;
  }

  // Helper method to create partial update from full settings
  createUpdateFromSettings(settings: UserSettingsResponse): UserSettingsUpdate {
    return {
      notifications: settings.notifications,
      language: settings.language,
      theme: settings.theme
    };
  }
}